import { useEffect, useState } from 'react';
import Script from 'next/script';
import { CONFIG } from '@frameio/config';
import {
  useCookieConsentLastUpdate,
  useIsSegmentLoaded,
} from '@frameio/segment/contexts';
import { loadSegmentOt } from '@frameio/segment/utils';
import { formatOneTrustElements } from '@frameio/segment-ot';
import { isE2E } from '@frameio/utils';

const ANALYTICS_FALLBACK_DELAY = 5000;

type AnalyticsProps = {
  /** Boolean describing whether or not to restrict which integrations are loaded */
  isRestricted: boolean;
  analyticsOption?: Record<string, any>;
};

export const Analytics = ({
  isRestricted,
  analyticsOption,
}: AnalyticsProps) => {
  const segmentId = CONFIG.thirdParty.segmentId || '';
  const { isSegmentLoaded, toggleSegmentLoaded } = useIsSegmentLoaded();
  const { updateCookieConsentLastUpdate } = useCookieConsentLastUpdate();
  const [startAnalytics, setStartAnalytics] = useState(false);

  useEffect(() => {
    const shouldStartAnalytics =
      !window.analytics && !isSegmentLoaded && !isE2E() && segmentId;

    if (shouldStartAnalytics) {
      formatOneTrustElements();

      // Delay Segment and Adobe Analytics loading until the browser idles
      // in order to prevent analytics to interfere with higher priority tasks.
      // Note: The events triggered before loading Segment are still
      // queued and will be sent once everything's loaded correctly.
      requestIdleCallback(
        () => {
          setStartAnalytics(true);
        },
        { timeout: ANALYTICS_FALLBACK_DELAY }
      );
    }
  }, [isSegmentLoaded, segmentId]);

  const onLoad = async () => {
    await loadSegmentOt(segmentId, isRestricted, analyticsOption);
    // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#ready
    analytics.ready(() => {
      toggleSegmentLoaded(true);
    });
  };

  // Events below are defined by adobe analytics,
  // https://github.com/Frameio/segment-ot/blob/a23e2fec8b9d41f66e8c47cd171bccdf00f9e6ca/src/index.js#L176
  useEffect(() => {
    const events = [
      'adobePrivacy:PrivacyConsent',
      'adobePrivacy:PrivacyCustom',
      'adobePrivacy:PrivacyReject',
    ];

    const asyncUpdateCookieConsentLastUpdate = async () => {
      if (isSegmentLoaded && analytics) {
        updateCookieConsentLastUpdate();
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, asyncUpdateCookieConsentLastUpdate);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, asyncUpdateCookieConsentLastUpdate);
      });
    };
  }, [updateCookieConsentLastUpdate, isSegmentLoaded]);

  if (startAnalytics) {
    return (
      <>
        <Script
          dangerouslySetInnerHTML={{
            __html: `!function(){window.fedsConfig = window.fedsConfig || {}; window.fedsConfig.privacy
            = window.fedsConfig.privacy || {};
            window.fedsConfig.privacy.otDomainId =
            '${CONFIG.thirdParty.oneTrustDomainId}';}();`,
          }}
          id="manage-cookies-script"
        />
        <Script
          id="privacy-script"
          onLoad={onLoad}
          src="https://www.adobe.com/etc.clientlibs/globalnav/clientlibs/base/privacy-standalone.js"
          strategy="lazyOnload"
        />
      </>
    );
  }

  return null;
};
